import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const ProveedorChartModal = ({ open, onClose, proveedorId, proveedorNombre, propiedades }) => {
  const [gastosData, setGastosData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && proveedorId) {
      cargarGastosProveedor();
    }
  }, [open, proveedorId]);

  const cargarGastosProveedor = async () => {
    setLoading(true);
    setError(null);
    try {
      const currentYear = new Date().getFullYear();
      const response = await axios.get(`${API}/gastos?proveedor_id=${proveedorId}&year=${currentYear}`);
      const gastos = response.data;
      
      // Procesar los datos
      const dataProcesada = procesarDatosGastos(gastos);
      setGastosData(dataProcesada);
    } catch (err) {
      console.error('Error fetching provider expenses:', err);
      setError('No se pudieron cargar los datos del proveedor.');
    } finally {
      setLoading(false);
    }
  };

  const procesarDatosGastos = (gastos) => {
    // Agrupar por propiedad_id
    const gastosPorPropiedad = {};
    
    gastos.forEach(gasto => {
      // Ignorar gastos sin fecha o monto
      if (!gasto.fecha || !gasto.monto) return;
      
      const propId = gasto.propiedad_id;
      if (!gastosPorPropiedad[propId]) {
        // Inicializar arreglo de 12 meses para esta propiedad
        gastosPorPropiedad[propId] = MESES.map((mes, index) => ({
          mes: mes,
          total: 0,
          mesIndex: index
        }));
      }
      
      // Extraer el mes (0-11) desde la fecha (YYYY-MM-DD)
      const fecha = new Date(gasto.fecha + 'T12:00:00');
      const mesIndex = fecha.getMonth();
      
      gastosPorPropiedad[propId][mesIndex].total += parseFloat(gasto.monto);
    });
    
    // Convertir el objeto a un arreglo para poder iterar en el render
    return Object.keys(gastosPorPropiedad).map(propId => {
      // Obtener el nombre de la propiedad desde la lista global
      const propEncontrada = propiedades.find(p => p.id_propiedad === propId);
      const nombrePropiedad = propEncontrada ? propEncontrada.nombre : 'Propiedad Desconocida';
      
      return {
        propiedadId: propId,
        nombrePropiedad,
        dataMeses: gastosPorPropiedad[propId]
      };
    }).sort((a, b) => a.nombrePropiedad.localeCompare(b.nombrePropiedad)); // Ordenar por nombre de propiedad alfabéticamente
  };

  const formatCurrency = (value) => {
    return `$${Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="font-semibold text-[#0E3A53] mb-1">{label}</p>
          <p className="text-[#FF7F6A] font-medium">
            Total: {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0E3A53]">
            Análisis Anual - {proveedorNombre} ({new Date().getFullYear()})
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto pr-2 py-4 space-y-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-[#0E3A53]/50">
              <Loader2 className="w-8 h-8 animate-spin mb-4 text-[#1AA1D8]" />
              <p>Cargando datos del proveedor...</p>
            </div>
          ) : error ? (
            <div className="text-center py-10 text-red-500 bg-red-50 rounded-lg">
              {error}
            </div>
          ) : gastosData.length === 0 ? (
            <div className="text-center py-20 bg-gray-50 rounded-xl border border-gray-100">
              <p className="text-[#0E3A53]/60 text-lg">Este proveedor no tiene gastos registrados en {new Date().getFullYear()}.</p>
            </div>
          ) : (
            gastosData.map(propData => (
              <div key={propData.propiedadId} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                <h3 className="text-lg font-bold text-[#0E3A53] mb-4 border-b pb-2">
                  {propData.nombrePropiedad}
                </h3>
                <div className="h-[250px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={propData.dataMeses}
                      margin={{ top: 10, right: 10, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                      <XAxis 
                        dataKey="mes" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6B7280', fontSize: 12 }}
                        dy={10}
                      />
                      <YAxis 
                        tickFormatter={(value) => `$${value >= 1000000 ? (value/1000000).toFixed(1) + 'M' : value >= 1000 ? (value/1000).toFixed(0) + 'k' : value}`}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6B7280', fontSize: 12 }}
                        dx={-10}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={{fill: '#F3F4F6'}} />
                      <Bar 
                        dataKey="total" 
                        fill="#1AA1D8" 
                        radius={[4, 4, 0, 0]}
                        barSize={32}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProveedorChartModal;
